import { Button } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import type { CloseBlockReason, CloseUnblockKind } from "@/modules/pos/lib/tillActions";

/**
 * T6 / D173 — A GREYED CLOSE SAYS WHY, ON BOTH TILLS, AND OFFERS ONE TAP.
 *
 * D22 is unchanged: the server refuses a close over a pending request, an
 * unsent basket or a QUEUED plate, and the till still greys the control. What
 * changed is that the grey is no longer silent — the dark till used to name it
 * only for a paid direct takeaway (`closeNote`), and the PAPER till never did,
 * so a cashier stood in front of a dead button with a guest waiting.
 *
 * The sentence is the first reason that holds (`closeBlockReason`), counted in
 * Arabic's dual and plural (`countedKey`). The tap is `closeUnblockAction`'s:
 * «بدء التحضير والإغلاق» where no paper starts the food; in a paper workflow
 * «طباعة التذكرة» as the bill printer's first choice (D182 — on the panel and
 * phone bar this note then draws only the hidden reason, naming the printer) and
 * «إعادة إرسال التذكرة» in the PAPER settle window. One component on the panel's foot, the narrow
 * summary bar and the PAPER settle sheet's keep-open footnote, so the three
 * surfaces cannot word it three ways.
 */
export function useCloseReasonText(reason: CloseBlockReason | null): string | null {
  const t = useTranslate();
  if (!reason) return null;
  const { count } = reason;
  switch (reason.kind) {
    case "pending":
      return t(
        countedKey(count, {
          one: (m) => m.pos.closeReasonPendingOne,
          two: (m) => m.pos.closeReasonPendingTwo,
          few: (m) => m.pos.closeReasonPendingFew,
          many: (m) => m.pos.closeReasonPending,
        }),
        { count },
      );
    case "drafts":
      return t(
        countedKey(count, {
          one: (m) => m.pos.closeReasonDraftsOne,
          two: (m) => m.pos.closeReasonDraftsTwo,
          few: (m) => m.pos.closeReasonDraftsFew,
          many: (m) => m.pos.closeReasonDrafts,
        }),
        { count },
      );
    case "queued":
      return t(
        countedKey(count, {
          one: (m) => m.pos.closeReasonQueuedOne,
          two: (m) => m.pos.closeReasonQueuedTwo,
          few: (m) => m.pos.closeReasonQueuedFew,
          many: (m) => m.pos.closeReasonQueued,
        }),
        { count },
      );
  }
}

/** The hidden reason's id on the bill's foot (one per screen), for the greyed button's description. */
export const closeBlockedReasonId = (surface: "panel" | "bar" | "settle") => `close-blocked-reason-${surface}`;

/**
 * The reason's words, with «تعذّر التسليم: …» when the close is a hand-over —
 * and, while an unprinted ticket holds it (`printHint`, 2026-10-20), where the
 * one tap lives now: «طباعة التذكرة» moved into the printer's choice.
 */
export function useCloseBlockedText(
  reason: CloseBlockReason | null,
  handOver = false,
  printHint = false,
): string | null {
  const t = useTranslate();
  const closeText = useCloseReasonText(reason);
  const text =
    handOver && closeText
      ? closeText.replace(t((m) => m.dispatch.closeReasonLead), t((m) => m.dispatch.handOverReasonLead))
      : closeText;
  return text && printHint ? `${text} — ${t((m) => m.pos.closePrintTicketHint)}` : text;
}

export function CloseBlockedNote({
  reason,
  action,
  onAction,
  isBusy,
  isLoading,
  surface,
  handOver = false,
}: {
  reason: CloseBlockReason;
  /** 2026-10-20 — the close hands the bag to its driver: «تعذّر التسليم: …». */
  handOver?: boolean;
  /** The one tap, or null when this session holds no rung that clears it. */
  action: CloseUnblockKind | null;
  onAction?: () => void;
  isBusy: boolean;
  isLoading: boolean;
  /** Which surface draws it — a test id suffix, so each can be asserted alone. */
  surface: "panel" | "bar" | "settle";
}) {
  const t = useTranslate();
  /* 2026-10-20 (user): no permanent «إعادة إرسال التذكرة» on the bill's own
     foot (panel, phone bar). This component draws only while the close is
     greyed, so in a paper workflow the tap it offers there is «طباعة
     التذكرة» — the line sheet's own words — and only while an unprinted
     ticket is what holds the close / hand-over (`closeUnblockAction`). The
     PAPER settle window keeps its «إعادة إرسال التذكرة». */
  /* 2026-10-20 (user, second ruling): on the bill's own foot (panel, phone
     bar) «طباعة التذكرة» is no longer a button of its own — it is the first
     choice behind the printer, which carries an attention dot. Here only the
     hidden reason stays, naming the printer. The settle window keeps its
     visible «إعادة إرسال التذكرة». */
  const ticketOnPrinter = surface !== "settle" && action === "resendTicket";
  const text = useCloseBlockedText(reason, handOver, ticketOnPrinter);
  /* 2026-10-20 (user): under the bill's own button (panel, phone bar) the
     reason is not drawn as a line — it is the greyed button's tooltip and,
     here, a visually-hidden description it points at. The settle window
     keeps its visible note. */
  const hidden = surface !== "settle";
  const tap = action && onAction && !ticketOnPrinter ? action : null;
  return (
    <div
      className={hidden && !tap ? "sr-only" : "flex basis-full flex-wrap items-center gap-2"}
      data-ui="close-blocked"
      data-testid={`close-blocked-${surface}`}
      data-reason={reason.kind}
    >
      <p
        id={closeBlockedReasonId(surface)}
        className={
          hidden
            ? "sr-only"
            : "min-w-0 flex-1 basis-[12rem] text-[length:var(--font-size-xs)] font-semibold text-[color:var(--text-muted)]"
        }
        role="status"
        data-hidden={hidden ? "" : undefined}
        data-testid={`close-blocked-reason-${surface}`}
      >
        {text}
      </p>
      {tap ? (
        <Button
          variant="outline"
          size="touch"
          className="shrink-0 rounded-[var(--radius-list)] font-bold"
          disabled={isBusy}
          isLoading={isLoading}
          data-testid={`close-unblock-${surface}`}
          data-kind={tap}
          onClick={onAction}
        >
          {tap === "startAndClose"
            ? surface === "settle"
              ? // The settle window's own Pay is the close: this tap only starts.
                t((m) => m.pos.closeStartOnly)
              : t((m) => m.pos.closeStartAndClose)
            : surface === "settle"
              ? t((m) => m.pos.closeResendTicket)
              : t((m) => m.pos.printChit)}
        </Button>
      ) : null}
    </div>
  );
}
